import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useDeleteFlow, useSaveFlow } from '../../components/confirm';
import { useHotkeys, formatCombo, effectiveCombo } from '../../components/hotkeys';
import { BusyIcon } from '../../components/loading';
import { useNotify } from '../../components/notifications';
import { guardedExit, resetUnsavedTracking, useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import {
  SDT_BASE, SDT_FORM_NAME, SDT_PAGE_ID, SDT_TABS,
  changedSdtRows, firstSdtProblem, newSdtRow, toSdtInput, toSdtRow,
  type SdtEditable, type SdtResultType, type SdtRow,
} from './scientificDataTable';
import './scientificDataTable.css';

/**
 * Settings ▸ SDMS ▸ Scientific Data Table (legacy `ScientificDataTableController`).
 *
 * Two lists over one table — Raw Data and Calibration — edited IN THE GRID, as
 * legacy does: **Add** puts one new row at the top of the grid, cells are edited in
 * place, **Save** stores every added and edited row of the tab in one go, **Delete**
 * removes the ticked rows. There is no detail view in legacy and none here, which
 * is why this is a bespoke page rather than the shared list + detail: it is built
 * from the shared grid, toolbar, title and save / delete flows only.
 *
 * On the Raw Data tab a field with Save In Table owns a column of
 * `SpreadSheetEntry_SampleInfo`; the API keeps that column in step and reports
 * what it did, which is shown after the save.
 */
export function ScientificDataTablePage({ onHome }: { onHome: () => void }) {
  const [tab, setTab] = useState<SdtResultType>('RawDataTable');
  // A tab is mounted on its first visit and then KEPT, so each tab holds its own
  // rows, unsaved edits, ticks and search while the user looks at the other one.
  const [visited, setVisited] = useState<Set<SdtResultType>>(new Set(['RawDataTable']));
  const [meta, setMeta] = useState<{ loadMs: number | null; loading: boolean }>({ loadMs: null, loading: true });
  const onMeta = useCallback((loadMs: number | null, loading: boolean) => setMeta({ loadMs, loading }), []);
  const show = (next: SdtResultType) => {
    setTab(next);
    setVisited((v) => (v.has(next) ? v : new Set(v).add(next)));
  };

  return (
    <div className="bl-page mlv-host">
      <div className="bl-grid-area">
        <div className="dp-breadcrumb">
          <a onClick={onHome} style={{ cursor: 'pointer' }}>Home</a>
          <i className="ti ti-chevron-right" />
          <span>Settings</span>
          <i className="ti ti-chevron-right" />
          <span>SDMS</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">Scientific Data Table</span>
        </div>

        <FormTitle pageId={SDT_PAGE_ID} loadMs={meta.loadMs} loading={meta.loading} auditFormName={SDT_FORM_NAME} />

        {/* Tabs FIRST; each tab then has its OWN action bar and grid — as legacy,
            where Raw Data and Calibration are two lists, each with its own
            Add · Save · Delete · Export to. */}
        <div className="sdt-bar" data-unsaved-ignore>
          <span className="sdt-tabs" role="tablist" aria-label="Data set">
            {SDT_TABS.map((t) => (
              <button
                key={t.value}
                type="button"
                role="tab"
                aria-selected={t.value === tab}
                className={t.value === tab ? 'on' : ''}
                onClick={() => show(t.value)}
              >
                {t.label}
              </button>
            ))}
          </span>
        </div>

        {SDT_TABS.filter((t) => visited.has(t.value)).map((t) => (
          <ScientificDataTab key={t.value} tab={t.value} active={t.value === tab} onMeta={onMeta} />
        ))}
      </div>
    </div>
  );
}

/**
 * ONE tab of the Scientific Data Table: its action bar, search and grid, and the
 * buffer of rows added / edited on it. Every action here acts on THIS tab only —
 * Add adds a row to it, Save stores its rows, Delete removes its ticked rows,
 * Refresh and Export read it. The other tab is a separate instance and is never
 * touched, so unsaved rows on one tab survive a visit to the other.
 */
function ScientificDataTab({ tab, active, onMeta }: {
  tab: SdtResultType;
  /** The tab on screen — only it owns the keyboard shortcuts and the load-time chip. */
  active: boolean;
  onMeta: (loadMs: number | null, loading: boolean) => void;
}) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();

  const [rows, setRows] = useState<SdtRow[]>([]);
  const [loaded, setLoaded] = useState<Map<string, SdtRow>>(new Map());
  const [dataTypes, setDataTypes] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  // Remounts the grid so a new row is on screen: page 1, no column filter hiding it.
  const [gridKey, setGridKey] = useState(0);
  const seq = useRef(0);
  const saveNotes = useRef<string[]>([]);

  const changed = useMemo(() => changedSdtRows(rows, loaded), [rows, loaded]);
  const dirty = changed.length > 0;
  // Rule 15: the grid buffer is state the automatic tracker cannot see.
  useUnsavedGuard(dirty);

  const load = useCallback(async (resultType: SdtResultType) => {
    const started = performance.now();
    setLoading(true);
    try {
      const res = await api.get<{ data?: { rows?: Record<string, unknown>[]; dataTypes?: string[] } }>(
        SDT_BASE, { params: { resultType } });
      const list = (res.data?.data?.rows ?? []).map(toSdtRow);
      setRows(list);
      setLoaded(new Map(list.map((r) => [r.key, r])));
      setDataTypes(res.data?.data?.dataTypes ?? []);
      setSelected(new Set());
      resetUnsavedTracking();
    } catch {
      // An empty grid must not pass for "no fields" when the list could not be read.
      notify.error('The fields could not be loaded. Check that the server is running, then press Refresh.');
    } finally {
      setLoading(false);
      setLoadMs(performance.now() - started);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void load(tab); }, [load, tab]);
  useEffect(() => { if (active) onMeta(loadMs, loading); }, [active, loadMs, loading, onMeta]);

  const patch = (key: string, p: Partial<SdtEditable>) =>
    setRows((d) => d.map((r) => (r.key === key ? { ...r, ...p } : r)));

  // ── Add: one new row at the top of the grid ────────────────────────────────
  const add = () => {
    seq.current += 1;
    setRows((d) => [newSdtRow(seq.current, tab), ...d]);
    setSearch('');
    setGridKey((k) => k + 1);
  };

  // ── Save: every added / edited row of this tab, one transaction ────────────
  const save = () => {
    if (!dirty || saving) return;
    const problem = firstSdtProblem(changed, tab);
    if (problem) { notify.warning(problem); return; }
    saveNotes.current = [];
    void runSave({
      formName: SDT_FORM_NAME,
      // A reason is asked for (when configured) only if a stored row was changed.
      auditAction: changed.some((r) => r.id != null) ? 'edit' : 'create',
      save: async () => {
        setSaving(true);
        try {
          const res = await api.post<{ data?: { notes?: string[] } }>(
            `${SDT_BASE}/save`, { resultType: tab, rows: changed.map(toSdtInput) });
          saveNotes.current = res.data?.data?.notes ?? [];
        } finally {
          setSaving(false);
        }
      },
      successMessage: () =>
        saveNotes.current.length ? `Saved successfully.\n${saveNotes.current.join('\n')}` : 'Saved successfully.',
      onSaved: () => { void load(tab); },
    });
  };

  // ── Delete: the ticked rows ────────────────────────────────────────────────
  const remove = () => {
    if (selected.size === 0) return;
    const picked = rows.filter((r) => selected.has(r.key));
    const stored = picked.filter((r) => r.id != null);
    const dropLocal = (keys: Set<string>) => {
      setRows((d) => d.filter((r) => !keys.has(r.key)));
      setLoaded((m) => { const n = new Map(m); keys.forEach((k) => n.delete(k)); return n; });
      setSelected(new Set());
    };
    // Rows never saved are simply taken out of the grid.
    if (stored.length === 0) { dropLocal(new Set(picked.map((r) => r.key))); return; }

    const notes: string[] = [];
    const done = new Set<string>(picked.filter((r) => r.id == null).map((r) => r.key));
    void runDelete({
      formName: SDT_FORM_NAME,
      message: stored.length === 1
        ? `Delete '${stored[0]!.fieldName}'?`
        : `Delete ${stored.length} fields?`,
      delete: async () => {
        try {
          for (const r of stored) {
            const res = await api.delete<{ data?: { note?: string | null } }>(`${SDT_BASE}/${r.id}`);
            done.add(r.key);
            const note = res.data?.data?.note;
            if (note) notes.push(note);
          }
        } finally {
          // Whatever was deleted before a refusal is gone — the grid must say so,
          // and the other rows' unsaved edits stay as they are.
          dropLocal(done);
        }
      },
      successMessage: null,
      onDeleted: () => { notify.success(['Deleted.', ...notes].join(' ')); },
    });
  };

  const refresh = () => { if (dirty) void guardedExit(() => { void load(tab); }); else void load(tab); };

  // Save is a form-specific button, so its key is registered here (rule 14).
  useHotkeys(!active ? [] : [
    { combo: 'mod+s', label: 'Save', group: 'List', order: 1, run: () => save(), disabled: !dirty || saving },
  ]);

  const check = (r: SdtRow, field: 'common' | 'saveInTable', label: string) => (
    <input
      type="checkbox"
      className="sdt-check"
      aria-label={`${label} — ${r.fieldName || 'new row'}`}
      checked={r[field]}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => patch(r.key, { [field]: e.target.checked })}
    />
  );

  const columns: AppColumn<SdtRow>[] = useMemo(() => {
    const base: AppColumn<SdtRow>[] = [
      { field: 'fieldName', header: 'FieldName', width: 260,
        edit: { type: 'text', requiredEmpty: (r) => !r.fieldName.trim() } },
      { field: 'captionEn', header: 'CaptionEN', width: 240, edit: { type: 'text' } },
      { field: 'captionCn', header: 'CaptionCN', width: 220, edit: { type: 'text' } },
      { field: 'dataType', header: 'DataType', width: 150,
        edit: { type: 'combo', options: dataTypes, clearable: true,
          requiredEmpty: (r) => tab === 'RawDataTable' && r.saveInTable && !r.dataType.trim() } },
      { field: 'common', header: 'Common', width: 90, align: 'center',
        value: (r) => (r.common ? 'Yes' : 'No'), text: (r) => (r.common ? 'Yes' : 'No'),
        render: (r) => check(r, 'common', 'Common') },
    ];
    // Save In Table exists on the Raw Data tab only (legacy hides it on Calibration).
    if (tab === 'RawDataTable') {
      base.push({ field: 'saveInTable', header: 'SaveInTable', width: 110, align: 'center',
        value: (r) => (r.saveInTable ? 'Yes' : 'No'), text: (r) => (r.saveInTable ? 'Yes' : 'No'),
        render: (r) => check(r, 'saveInTable', 'Save In Table') });
    }
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTypes, tab]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    // A row not saved yet stays visible so it cannot be "lost" behind a search.
    return rows.filter((r) => r.id == null
      || [r.fieldName, r.captionEn, r.captionCn, r.dataType].some((v) => v.toLowerCase().includes(q)));
  }, [rows, search]);

  const tabLabel = SDT_TABS.find((t) => t.value === tab)!.label;

  return (
        <div className="sdt-panel" role="tabpanel" aria-label={tabLabel} hidden={!active}>
        <AppToolbar
          noHotkeys={!active}
          showMeta={active}
          newFirst
          newLabel="Add"
          onNew={add}
          newDisabled={loading || saving}
          left={(
            <button
              className="ca-btn ca-btn-solid"
              onClick={save}
              disabled={!dirty || saving}
              title={`Save (${formatCombo(effectiveCombo('mod+s'))})`}
            >
              {saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} Save
            </button>
          )}
          onDelete={remove}
          deleteDisabled={selected.size === 0 || saving}
          onRefresh={refresh}
          refreshing={loading}
          exportData={{ filename: `Scientific Data Table - ${tabLabel}`, ...gridExportMatrix(shown, columns) }}
          // The search box rides on the action bar, at the shared control size.
          right={(
            <span className="sdt-search" data-unsaved-ignore>
              {/* The standard list search box (same markup as every list form). */}
              <div className="list-search">
                <span className="tb-i">🔍</span>
                <input
                  type="text"
                  placeholder="Search…"
                  aria-label="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <span className="list-search-clear" onClick={() => setSearch('')}>×</span>
                )}
              </div>
              {dirty && (
                <span className="sdt-pending">
                  {changed.length} unsaved {changed.length === 1 ? 'row' : 'rows'}
                </span>
              )}
            </span>
          )}
        />

        <AppDataGrid<SdtRow>
          key={gridKey}
          rows={shown}
          columns={columns}
          getRowId={(r) => r.key}
          loading={loading}
          selectable
          selectedIds={selected}
          onSelectionChange={setSelected}
          onCellCommit={(row, col, v) => patch(row.key, { [col.field]: v } as Partial<SdtEditable>)}
          onCellChange={(row, col, v) => patch(row.key, { [col.field]: v } as Partial<SdtEditable>)}
          getRowClassName={(r) => (r.id == null ? 'sdt-row-new' : undefined)}
          defaultPageSize={20}
          exportFilename={`Scientific Data Table - ${tabLabel}`}
          emptyText="No fields — use Add to create one"
        />
        </div>
  );
}
